/**
 * treeLook.js - how the main project and sub-project rows look (Sharif,
 * 10/14/26: "add that parent row as a customizable feature"). Part of a saved
 * view (viewConfig `tree`, server/routes/tableViews.js cleanTree keeps the
 * same shape), shared with it like the other look settings. PURE.
 *
 *   tree = {
 *     main: LEVEL, sub: LEVEL,   the group rows of each level
 *     lines: bool,               the connectors from a record to its group
 *     open: 'remember' | 'expanded' | 'collapsed'   how groups start
 *   }
 *   LEVEL = {
 *     fill    'default' (the green band) | 'none' (white) | a fill preset | '#RRGGBB'
 *     font    'default' | a font preset | '#RRGGBB'
 *     bold    bool        size  px (9-14) or null (the table's own)
 *     number  the project # badge      totals  the roll-up figures
 *     count   the record count badge
 *   }
 * A view saved before this had none: it gets DEFAULT_TREE - the rows as
 * they looked when the tree shipped.
 */
import { HIGHLIGHT_COLORS, FONT_COLORS } from './highlights';

const HEX = /^#[0-9a-fA-F]{6}$/;
export const TREE_FILLS = { main: '#E8F1DF', sub: '#F1F6EB' }; // the default bands
export const TREE_OPEN = ['remember', 'expanded', 'collapsed'];
export const TREE_SIZES = [9, 9.5, 10, 10.5, 11, 11.5, 12, 13, 14];
const LEVEL = Object.freeze({ fill: 'default', font: 'default', bold: true, size: null, number: true, totals: true, count: true });
export const DEFAULT_TREE = Object.freeze({ main: LEVEL, sub: LEVEL, lines: true, open: 'remember' });

const bool = (v, d) => (typeof v === 'boolean' ? v : d);
const color = (v, presets, extra) => {
  if (typeof v === 'string' && HEX.test(v)) return v.toUpperCase();
  return extra.includes(v) || Object.prototype.hasOwnProperty.call(presets, v) ? v : 'default';
};
function cleanLevel(l) {
  const x = l && typeof l === 'object' && !Array.isArray(l) ? l : {};
  const n = Number(x.size);
  return {
    fill: color(x.fill, HIGHLIGHT_COLORS, ['default', 'none']),
    font: color(x.font, FONT_COLORS, ['default']),
    bold: bool(x.bold, LEVEL.bold),
    size: x.size != null && x.size !== '' && Number.isFinite(n) && n > 0 ? Math.min(14, Math.max(9, Math.round(n * 2) / 2)) : null,
    number: bool(x.number, LEVEL.number),
    totals: bool(x.totals, LEVEL.totals),
    count: bool(x.count, LEVEL.count),
  };
}

/** A view's tree look with every field present and valid. */
export function cleanTree(tree) {
  const t = tree && typeof tree === 'object' && !Array.isArray(tree) ? tree : {};
  return {
    main: cleanLevel(t.main),
    sub: cleanLevel(t.sub),
    lines: bool(t.lines, DEFAULT_TREE.lines),
    open: TREE_OPEN.includes(t.open) ? t.open : DEFAULT_TREE.open,
  };
}

/** One level's look, resolved: { fill: '#RRGGBB', font: '#RRGGBB' | null, bold, size, number, totals, count }. */
export function levelLook(tree, level) {
  const l = cleanTree(tree)[level === 'sub' ? 'sub' : 'main'];
  const fill = HEX.test(l.fill)
    ? l.fill
    : l.fill === 'none'
      ? '#FFFFFF'
      : HIGHLIGHT_COLORS[l.fill]
        ? HIGHLIGHT_COLORS[l.fill].fill
        : TREE_FILLS[level === 'sub' ? 'sub' : 'main'];
  const font = HEX.test(l.font) ? l.font : FONT_COLORS[l.font] ? FONT_COLORS[l.font].fill : null;
  return { ...l, fill, font };
}

/** The look the coloured Excel export gives a main / sub-project record's row: its group row's fill, font and weight. */
export function exportLevelLook(tree, level) {
  const l = levelLook(tree, level);
  return { fill: l.fill, ...(l.font ? { font: l.font } : {}), ...(l.bold ? { bold: true } : {}) };
}
